import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Switch,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Autocomplete,
  Chip,
} from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import {
  CloudUpload,
  CheckCircle,
  EventBusy,
  Close,
  CancelOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Configure dayjs for IST timezone
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Kolkata");

const MultiSelectDay = ({
  day,
  pendingDateKeys = [],
  addedDateKeys = [],
  recoveryDateKeys = [],
  outsideCurrentMonth,
  ...other
}) => {
  const dateKey = day.format("YYYY-MM-DD");
  const isPending = pendingDateKeys.includes(dateKey);
  const isAdded = addedDateKeys.includes(dateKey);
  const isRecoveryDate = recoveryDateKeys.includes(dateKey);

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={isPending || isAdded}
      sx={{
        ...(isRecoveryDate &&
          !isPending &&
          !isAdded && {
            backgroundColor: "rgba(76, 175, 80, 0.14)",
            color: "#2e7d32",
            fontWeight: 700,
            border: "1px solid rgba(76, 175, 80, 0.45)",
          }),
        ...(isAdded && {
          backgroundColor: isRecoveryDate ? "#4CAF50 !important" : "#5e72e4 !important",
          color: "#fff !important",
        }),
        ...(isPending && {
          backgroundColor: isRecoveryDate ? "#2e7d32 !important" : "#11cdef !important",
          color: "#fff !important",
          boxShadow: isRecoveryDate
            ? "0 0 0 2px rgba(76, 175, 80, 0.25)"
            : "0 0 0 2px rgba(17, 205, 239, 0.25)",
        }),
        "&:hover": {
          backgroundColor:
            isPending || isAdded
              ? undefined
              : isRecoveryDate
              ? "rgba(76, 175, 80, 0.22)"
              : "rgba(94, 114, 228, 0.12)",
        },
      }}
    />
  );
};

MultiSelectDay.propTypes = {
  day: PropTypes.object.isRequired,
  pendingDateKeys: PropTypes.arrayOf(PropTypes.string),
  addedDateKeys: PropTypes.arrayOf(PropTypes.string),
  recoveryDateKeys: PropTypes.arrayOf(PropTypes.string),
  outsideCurrentMonth: PropTypes.bool,
};

const MultiDatePicker = ({
  pendingDates,
  addedDates,
  recoveryDateKeys = [],
  onPendingDatesChange,
  maxDate,
}) => {
  const pickerValue =
    pendingDates.length > 0 ? dayjs(pendingDates[pendingDates.length - 1]) : null;

  const handleDateToggle = (newValue) => {
    if (!newValue || !newValue.isValid()) return;

    const dateKey = newValue.format("YYYY-MM-DD");
    if (addedDates.includes(dateKey)) {
      toast.info("This date is already added. Remove it from the list to select it again.");
      return;
    }

    onPendingDatesChange((currentDates) =>
      currentDates.includes(dateKey)
        ? currentDates.filter((date) => date !== dateKey)
        : [...currentDates, dateKey].sort()
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={pickerValue}
        onChange={handleDateToggle}
        maxDate={maxDate}
        format="DD/MM/YYYY"
        closeOnSelect={false}
        slots={{ day: MultiSelectDay }}
        slotProps={{
          day: {
            pendingDateKeys: pendingDates,
            addedDateKeys: addedDates,
            recoveryDateKeys,
          },
          actionBar: {
            actions: [],
          },
          textField: {
            size: "small",
            placeholder: "Select multiple dates",
            helperText:
              pendingDates.length > 0
                ? `${pendingDates.length} date${pendingDates.length > 1 ? "s" : ""} ready to add`
                : "Calendar will stay open while you select dates",
            sx: {
              width: "100%",
              "& .MuiInputBase-root": {
                minHeight: "45px",
                borderRadius: "8px",
              },
              "& .MuiFormHelperText-root": {
                ml: 0,
                color: pendingDates.length > 0 ? "#11cdef" : "#67748e",
                fontWeight: pendingDates.length > 0 ? 600 : 400,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

MultiDatePicker.propTypes = {
  pendingDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  addedDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  recoveryDateKeys: PropTypes.arrayOf(PropTypes.string),
  onPendingDatesChange: PropTypes.func.isRequired,
  maxDate: PropTypes.object,
};

const MarkAttendanceModal = ({
  open,
  onClose,
  onSubmit,
  initialData = {},
  loading = false,
  classId,
  classDetails,
  attendanceList = [],
}) => {
  const [formType, setFormType] = useState("attendance"); // "attendance" or "leave"
  const [leaveFor, setLeaveFor] = useState("TUTOR"); // "TUTOR" or "PARENT"
  const [selectedLeaveDates, setSelectedLeaveDates] = useState([]); // Array for multiple dates
  const [pendingLeaveDates, setPendingLeaveDates] = useState([]);

  // New states for flexible attendance
  const [pendingAttendanceDates, setPendingAttendanceDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]); // Multiple dates array
  const [selectedTypes, setSelectedTypes] = useState([]); // Multiple types array
  const [startTime, setStartTime] = useState(""); // Start time for new attendance
  const [endTime, setEndTime] = useState(""); // End time for new attendance
  const [allowDueCycle, setAllowDueCycle] = useState(false);

  // Attendance type options
  const attendanceTypeOptions = [
    { value: "REGULAR", label: "Regular Class" },
    { value: "EXTRA", label: "Extra Class" },
    { value: "RECOVERY", label: "Recovery Class" },
  ];

  const [formData, setFormData] = useState({
    attendanceTime: initialData.attendanceTime || new Date().toISOString(),
    topicCovered: initialData.topicCovered || "",
    homeWork: initialData.homeWork || null,
    voiceNote: initialData.voiceNote || null,
    leaveReason: "",
  });

  const consumedRecoveryDateKeys = new Set(
    (Array.isArray(attendanceList) ? attendanceList : [])
      .filter((attendance) => {
        const attendanceTypes = Array.isArray(attendance?.type)
          ? attendance.type
          : attendance?.type
          ? [attendance.type]
          : [];
        return attendanceTypes.includes("RECOVERY");
      })
      .map((attendance) =>
        dayjs(attendance?.recoveryClassDate || attendance?.attendanceTime || attendance?.startTime).format(
          "YYYY-MM-DD"
        )
      )
      .filter(Boolean)
  );

  const recoveryDateKeys = Array.isArray(classDetails?.leaveDate)
    ? classDetails.leaveDate
        .filter((leaveItem) => leaveItem?.notCounted && leaveItem?.date)
        .map((leaveItem) => dayjs(leaveItem.date).format("YYYY-MM-DD"))
        .filter((dateKey) => dateKey && !consumedRecoveryDateKeys.has(dateKey))
    : [];

  const getRecoveryForSelectedDates = () => {
    if (!selectedTypes.includes("RECOVERY")) return undefined;

    const matchingLeave = Array.isArray(classDetails?.leaveDate)
      ? classDetails.leaveDate.find((leaveItem) => {
          if (!leaveItem?.date || !["PARENT", "TUTOR"].includes(leaveItem?.leaveFor)) {
            return false;
          }

          return selectedDates.includes(dayjs(leaveItem.date).format("YYYY-MM-DD"));
        })
      : null;

    return matchingLeave?.leaveFor || "PARENT";
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData((prev) => ({ ...prev, [field]: file }));
    }
  };

  const handleSubmit = () => {
    if (formType === "attendance") {
      // Flexible mode - new attendance for any date
      if (selectedDates.length === 0) {
        toast.error("Please select at least one date");
        return;
      }

      if (!selectedTypes || selectedTypes.length === 0) {
        toast.error("Please select at least one attendance type");
        return;
      }

      if (!formData.topicCovered.trim()) {
        toast.error("Please enter topics covered");
        return;
      }

      if (!startTime || !endTime) {
        toast.error("Please select both Start Time and End Time");
        return;
      }

      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);
      const selectedDuration = endHours * 60 + endMinutes - (startHours * 60 + startMinutes);
      const classDuration = Number(classDetails?.duration || 60);
      const includesRegularAndExtra =
        selectedTypes.includes("REGULAR") && selectedTypes.includes("EXTRA");
      const includesRegularAndRecovery =
        selectedTypes.includes("REGULAR") && selectedTypes.includes("RECOVERY");
      const requiredDuration = classDuration *
        (1 + (includesRegularAndExtra ? 1 : 0) + (includesRegularAndRecovery ? 1 : 0));
      const minimumDuration = Math.max(requiredDuration - 10, 1);

      if (selectedDuration < minimumDuration) {
        toast.error(
          `Selected duration is ${selectedDuration} minutes. This selection requires at least ${minimumDuration} minutes.`
        );
        return;
      }

      const extraClassCount = includesRegularAndExtra
        ? Math.max(1, Math.round(selectedDuration / classDuration) - 1 - (includesRegularAndRecovery ? 1 : 0))
        : undefined;
      // Convert start and end time to IST timezone ISO format
      let convertedStartTime = "";
      let convertedEndTime = "";

      if (startTime && selectedDates.length > 0) {
        // Use first date as reference for time conversion
        const referenceDate = selectedDates[0];
        const [hours, minutes] = startTime.split(":");
        // Create date in IST timezone
        const startDateTime = dayjs.tz(`${referenceDate}T${startTime}:00`, "Asia/Kolkata");
        convertedStartTime = startDateTime.toISOString();
      }

      if (endTime && selectedDates.length > 0) {
        const referenceDate = selectedDates[0];
        const [hours, minutes] = endTime.split(":");
        // Create date in IST timezone
        const endDateTime = dayjs.tz(`${referenceDate}T${endTime}:00`, "Asia/Kolkata");
        convertedEndTime = endDateTime.toISOString();
      }

      // Create flexible attendance data with multiple dates
      const submitData = {
        ...formData,
        classSessionId: classId,
        attendanceDate: selectedDates, // Send array of dates
        startTime: convertedStartTime, // Send IST timezone ISO format
        endTime: convertedEndTime, // Send IST timezone ISO format
        type: selectedTypes, // Send array of selected types
        extraClassCount,
        recoveryFor: getRecoveryForSelectedDates(),
        isFlexible: true,
        allowDueCycle,
      };

      console.log("Flexible attendance submit data:", submitData);
      onSubmit(submitData);
    } else {
      // Leave validation
      if (selectedLeaveDates.length === 0) {
        toast.error("Please select at least one date");
        return;
      }

      if (!leaveFor) {
        toast.error("Please select Leave For");
        return;
      }

      console.log("=== Submit Leave Data Debug ===");
      console.log("Selected dates:", selectedLeaveDates);
      console.log("Leave For:", leaveFor);
      console.log("Class ID:", classId);

      // Convert dates to ISO format
      const formattedDates = selectedLeaveDates.map((date) => date + "T00:00:00.000Z");

      // For leave, use different payload structure
      const leaveData = {
        classSessionId: classId,
        dates: formattedDates,
        leaveFor: leaveFor,
        isLeave: true,
      };
      console.log("Leave submit data:", leaveData);
      console.log("=========================");
      onSubmit(leaveData);
    }
  };

  const handleClose = () => {
    setFormData({
      attendanceTime: new Date().toISOString(),
      topicCovered: "",
      homeWork: null,
      voiceNote: null,
      leaveReason: "",
    });
    setSelectedLeaveDates([]);
    setPendingLeaveDates([]);
    setFormType("attendance");
    setLeaveFor("TUTOR");
    setPendingAttendanceDates([]);
    setSelectedDates([]);
    setSelectedTypes([]);
    setStartTime("");
    setEndTime("");
    setAllowDueCycle(false);
    onClose();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "No Date";

    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 2.5,
          position: "relative",
        }}
      >
        <ArgonBox>
          <ArgonTypography variant="h5" color="white" fontWeight="bold">
            Mark Attendance / Leave
          </ArgonTypography>
          <ArgonTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
            Select one or more dates and mark attendance or leave for the class
          </ArgonTypography>
        </ArgonBox>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white !important",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <ArgonBox display="flex" flexDirection="column" gap={3}>
          {/* Form Type Selection */}
          <ArgonBox>
            <FormControl component="fieldset">
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, mt: 1, display: "block" }}
              >
                Select Type
              </ArgonTypography>
              <RadioGroup
                row
                value={formType}
                onChange={(e) => {
                  setFormType(e.target.value);
                  setFormData((prev) => ({ ...prev, topicCovered: "", leaveReason: "" }));
                }}
                sx={{ gap: 2, ml: 1.4 }}
              >
                <FormControlLabel
                  value="attendance"
                  control={
                    <Radio
                      sx={{
                        color: "#4CAF50",
                        "&.Mui-checked": {
                          color: "#4CAF50",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  }
                  label={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <CheckCircle sx={{ fontSize: 18, color: "#4CAF50" }} />
                      <ArgonTypography variant="button" fontWeight="medium">
                        MARK ATTENDANCE
                      </ArgonTypography>
                    </ArgonBox>
                  }
                  sx={{
                    border: formType === "attendance" ? "2px solid #4CAF50" : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    mr: 0,
                    backgroundColor: formType === "attendance" ? "#f1f8f4" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                />
                <FormControlLabel
                  value="leave"
                  control={
                    <Radio
                      sx={{
                        color: "#f44336",
                        "&.Mui-checked": {
                          color: "#f44336",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  }
                  label={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <EventBusy sx={{ fontSize: 18, color: "#f44336" }} />
                      <ArgonTypography variant="button" fontWeight="medium">
                        MARK LEAVE
                      </ArgonTypography>
                    </ArgonBox>
                  }
                  sx={{
                    border: formType === "leave" ? "2px solid #f44336" : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    backgroundColor: formType === "leave" ? "#fef5f5" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                />
              </RadioGroup>
            </FormControl>
          </ArgonBox>

          {/* Date Selection - Different UI for Attendance vs Leave */}
          {formType === "attendance" ? (
            <>
              {/* New Attendance - Calendar Date Picker and Type Selection */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Select Dates *
                </ArgonTypography>

                {/* Multi-date input with Add button */}
                <ArgonBox display="flex" gap={1} mb={2} alignItems="flex-start">
                  <ArgonBox sx={{ width: "100%" }}>
                    <MultiDatePicker
                      pendingDates={pendingAttendanceDates}
                      addedDates={selectedDates}
                      recoveryDateKeys={recoveryDateKeys}
                      onPendingDatesChange={setPendingAttendanceDates}
                      maxDate={dayjs().tz("Asia/Kolkata")}
                    />
                  </ArgonBox>
                  <ArgonButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      setSelectedDates((currentDates) =>
                        [...new Set([...currentDates, ...pendingAttendanceDates])].sort()
                      );
                      setPendingAttendanceDates([]);
                    }}
                    disabled={pendingAttendanceDates.length === 0}
                    sx={{
                      minWidth: "120px",
                      height: "45px",
                      flexShrink: 0,
                    }}
                  >
                    Add Dates
                    {pendingAttendanceDates.length > 0
                      ? ` (${pendingAttendanceDates.length})`
                      : ""}
                  </ArgonButton>
                </ArgonBox>

                {/* Selected Dates List */}
                {selectedDates.length > 0 && (
                  <ArgonBox
                    sx={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      p: 2,
                      border: "1px solid #e0e0e0",
                      mb: 2,
                    }}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                      Selected Dates ({selectedDates.length}):
                    </ArgonTypography>
                    <ArgonBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {selectedDates.map((date) => (
                        <ArgonBox
                          key={date}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            backgroundColor: "white",
                            border: recoveryDateKeys.includes(date)
                              ? "1px solid #4CAF50"
                              : "1px solid #5e72e4",
                            borderRadius: "8px",
                            px: 2,
                            py: 1,
                          }}
                        >
                          <ArgonTypography variant="caption" fontWeight="medium">
                            {new Date(date).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </ArgonTypography>
                          <ArgonButton
                            variant="text"
                            color="error"
                            size="small"
                            onClick={() => {
                              setSelectedDates(selectedDates.filter((d) => d !== date));
                            }}
                            sx={{ minWidth: "auto", p: 0, fontSize: "18px" }}
                          >
                            ×
                          </ArgonButton>
                        </ArgonBox>
                      ))}
                    </ArgonBox>
                  </ArgonBox>
                )}

                {recoveryDateKeys.length > 0 && (
                  <ArgonBox
                    mt={1}
                    sx={{
                      backgroundColor: "#f1f8f4",
                      border: "1px solid #a5d6a7",
                      borderRadius: "8px",
                      p: 1.2,
                    }}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="success">
                      Green dates show recovery-eligible class dates.
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonBox>

              {/* Type Selection with Multi-Select Autocomplete */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Attendance Type *
                </ArgonTypography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  id="attendance-type-autocomplete"
                  options={attendanceTypeOptions}
                  value={attendanceTypeOptions.filter((option) =>
                    selectedTypes.includes(option.value)
                  )}
                  onChange={(event, newValue) => {
                    setSelectedTypes(newValue.map((option) => option.value));
                  }}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select attendance types"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: "45px",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        deleteIcon={<CancelOutlined />}
                        sx={{
                          backgroundColor:
                            option.value === "REGULAR"
                              ? "#4CAF50"
                              : option.value === "EXTRA"
                              ? "#FF9800"
                              : "#2196F3",
                          color: "white",
                          fontWeight: "medium",
                          "& .MuiChip-deleteIcon": {
                            color: "white",
                            "&:hover": {
                              color: "#f0f0f0",
                            },
                          },
                        }}
                      />
                    ))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </ArgonBox>

              {/* Multiple Types Note */}
              {selectedTypes.length > 1 && (
                <ArgonBox
                  mt={1.5}
                  sx={{
                    backgroundColor: "#E3F2FD",
                    border: "1px solid #2196F3",
                    borderRadius: "8px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 16, color: "#2196F3" }} />
                  <ArgonTypography
                    variant="caption"
                    fontWeight="medium"
                    color="info"
                    fontSize="11px"
                  >
                    Important: Please provide Start Time and End Time according to each Type
                  </ArgonTypography>
                </ArgonBox>
              )}

              {/* Required Start and End Time */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Start Time *
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 45,
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      End Time *
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="time"
                      value={endTime}
                      onChange={(e) => {
                        const newEndTime = e.target.value;
                        // Validate that end time is not less than start time
                        if (startTime && newEndTime && newEndTime < startTime) {
                          toast.error("End time cannot be less than start time");
                          return;
                        }
                        setEndTime(newEndTime);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: startTime || undefined,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 45,
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </ArgonBox>
                </Grid>
              </Grid>
            </>
          ) : (
            /* Calendar Date Picker for Leave - Multiple dates */
            <ArgonBox>
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, display: "block" }}
              >
                Select Leave Dates *
              </ArgonTypography>

              {/* Multi-date input with Add button */}
              <ArgonBox display="flex" gap={1} mb={2} alignItems="flex-start">
                <ArgonBox sx={{ width: "100%" }}>
                  <MultiDatePicker
                    pendingDates={pendingLeaveDates}
                    addedDates={selectedLeaveDates}
                    onPendingDatesChange={setPendingLeaveDates}
                    maxDate={dayjs().tz("Asia/Kolkata")}
                  />
                </ArgonBox>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    setSelectedLeaveDates((currentDates) =>
                      [...new Set([...currentDates, ...pendingLeaveDates])].sort()
                    );
                    setPendingLeaveDates([]);
                  }}
                  disabled={pendingLeaveDates.length === 0}
                  sx={{
                    minWidth: "120px",
                    height: "45px",
                    flexShrink: 0,
                  }}
                >
                  Add Dates
                  {pendingLeaveDates.length > 0 ? ` (${pendingLeaveDates.length})` : ""}
                </ArgonButton>
              </ArgonBox>

              {/* Selected Dates List */}
              {selectedLeaveDates.length > 0 && (
                <ArgonBox
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    p: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                    Selected Dates ({selectedLeaveDates.length}):
                  </ArgonTypography>
                  <ArgonBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {selectedLeaveDates.map((date) => (
                      <ArgonBox
                        key={date}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: "white",
                          border: "1px solid #5e72e4",
                          borderRadius: "8px",
                          px: 2,
                          py: 1,
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          {new Date(date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </ArgonTypography>
                        <ArgonButton
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            setSelectedLeaveDates(selectedLeaveDates.filter((d) => d !== date));
                          }}
                          sx={{ minWidth: "auto", p: 0, fontSize: "18px" }}
                        >
                          ×
                        </ArgonButton>
                      </ArgonBox>
                    ))}
                  </ArgonBox>
                </ArgonBox>
              )}
            </ArgonBox>
          )}

          {/* Conditional Fields Based on Form Type */}
          {formType === "attendance" ? (
            <>
              {/* Topic Covered */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Topics Covered *
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter topics discussed in this session..."
                  value={formData.topicCovered}
                  onChange={(e) => handleChange("topicCovered", e.target.value)}
                />
              </ArgonBox>

              {/* File Uploads */}
              <Grid container spacing={2}>
                {/* Homework Upload */}
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Homework (Optional)
                    </ArgonTypography>
                    <ArgonBox
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d2d6da",
                        borderRadius: 2,
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#5e72e4",
                          background: "#f8f9fe",
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                      <ArgonTypography variant="caption" color="text" textAlign="center">
                        {formData.homeWork ? formData.homeWork.name : "Upload PDF/Image"}
                      </ArgonTypography>
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange("homeWork", e.target.files[0])}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Voice Note Upload */}
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Voice Note (Optional)
                    </ArgonTypography>
                    <ArgonBox
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d2d6da",
                        borderRadius: 2,
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#5e72e4",
                          background: "#f8f9fe",
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                      <ArgonTypography variant="caption" color="text" textAlign="center">
                        {formData.voiceNote ? formData.voiceNote.name : "Upload Audio"}
                      </ArgonTypography>
                      <input
                        type="file"
                        hidden
                        accept="audio/*"
                        onChange={(e) => handleFileChange("voiceNote", e.target.files[0])}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>
            </>
          ) : (
            /* Leave Section */
            <>
              {/* Leave For Dropdown */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Leave For *
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={leaveFor}
                  options={[
                    { value: "TUTOR", label: "Tutor" },
                    { value: "PARENT", label: "Parent" },
                  ]}
                  onChange={(e) => setLeaveFor(e.target.value)}
                  width="100%"
                />
              </ArgonBox>
            </>
          )}
        </ArgonBox>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          gap: 1.5,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        {formType === "attendance" && (
          <ArgonBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={allowDueCycle}
              onChange={(event) => setAllowDueCycle(event.target.checked)}
              color="warning"
            />
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              Allow Due Cycle (exceed total classes)
            </ArgonTypography>
          </ArgonBox>
        )}

        <ArgonBox
          sx={{
            display: "flex",
            gap: 1.5,
            flex: "1 1 auto",
            justifyContent: "flex-end",
            minWidth: "260px",
          }}
        >
          <ArgonButton
            onClick={handleClose}
            variant="outlined"
            color="secondary"
            disabled={loading}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="gradient"
            color={formType === "attendance" ? "success" : "error"}
            onClick={handleSubmit}
            disabled={
              loading ||
              (formType === "attendance"
                ? selectedDates.length === 0 || selectedTypes.length === 0
                : selectedLeaveDates.length === 0)
            }
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : formType === "attendance" ? (
              "Submit Attendance"
            ) : (
              "Submit Leave"
            )}
          </ArgonButton>
        </ArgonBox>
      </DialogActions>
    </Dialog>
  );
};

MarkAttendanceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
  classId: PropTypes.string,
  classDetails: PropTypes.object,
  attendanceList: PropTypes.array,
};

export default MarkAttendanceModal;
